import React, { useContext, useEffect, useState } from 'react'
import { NameDto } from '@/api/Common/Types'
import { PermissionsEnum } from '@/api/Identity/Types/PermissionsEnum'
import { NPaymentProviderDto } from '@/api/NPayments/Types/NPaymentProviderDto'
import {
  FormBaseButtonActionWrapper,
  FormBaseNavigationHeader,
} from '@/components/Form/Form.styles'
import { MultiSelect, Option } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { fetchClientLanguagesAction } from '@/store/clients/clients.actions'
import { ClientsApiClient } from '@/utils/api/clientsApi'
import { AuthSessionContext } from '@/utils/AuthContext'
import { isEmpty } from '@/utils/is-empty'
import { hasPermission } from '@/utils/permissionsValidator'
import { Form, Formik, FormikProps } from 'formik'
import { ChevronDown } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import { useApiClient } from 'utils/api/ApiClient'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'

const filterExistingLanguages = (
  allLanguages: NameDto<string>[] = [],
  existingLanguages: NameDto<string>[] = []
): NameDto<string>[] => {
  return allLanguages.filter(
    (lang) => !existingLanguages.some((existingLang) => existingLang.id === lang.id)
  )
}

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)

  return {
    getLanguagesAction: () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
  }
}

interface OuterProps {
  data: NPaymentProviderDto
  handleSubmit(config: NPaymentProviderDto): void
  isEdit: boolean
  setEdit: React.Dispatch<React.SetStateAction<boolean>>
}

interface FormValues {
  languages: NameDto<string>[]
}

export const PaymentProviderLanguagesDetailsPage: React.FC<OuterProps> = (props) => {
  const [auth] = useContext(AuthSessionContext)
  const [auditLogVisible, setAuditLogVisible] = useState<boolean>(false)
  const permissions = auth?.permissions || []
  const { data, handleSubmit, isEdit, setEdit } = props
  const { languages } = useMapStateToProps()
  const { getLanguagesAction } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(languages)) {
      getLanguagesAction()
    }
  }, [languages, getLanguagesAction])

  const initialValues: FormValues = {
    languages: data.languages,
  }

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          paymentProviderId={data.id}
        />
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={async (values, actions) => {
          handleSubmit({ ...data, languages: values.languages })
          actions.setSubmitting(false)
        }}
      >
        {(formikProps: FormikProps<FormValues>) => {
          const availableLanguages = filterExistingLanguages(
            languages,
            formikProps.values.languages
          )

          return (
            <Form>
              <FormBaseNavigationHeader>
                <FormBaseButtonActionWrapper style={{ margin: '1rem' }}>
                  <ButtonGroup>
                    {!isEdit ? (
                      <Button
                        type='button'
                        disabled={
                          !hasPermission(permissions, PermissionsEnum.CanEditPaymentProviders)
                        }
                        onClick={() => setEdit(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <>
                        <Button
                          type='button'
                          variant='outline'
                          onClick={() => {
                            setEdit(false)
                            formikProps.resetForm()
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type='submit'>Save</Button>
                      </>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            type='button'
                            variant='outline'
                            size='icon'
                            aria-label='More options'
                          />
                        }
                      >
                        <ChevronDown />
                      </DropdownMenuTrigger>

                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem
                          onSelect={() => setAuditLogVisible(true)}
                          data-test='audit-log-button'
                        >
                          Audit Log
                        </DropdownMenuItem>

                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger data-test='status-menu-item'>
                            Status
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              disabled={
                                !isEdit
                                  ? data.enabled ||
                                    !hasPermission(
                                      permissions,
                                      PermissionsEnum.CanEditPaymentProviders
                                    )
                                  : data.enabled
                              }
                              onSelect={() => {
                                handleSubmit({
                                  ...data,
                                  enabled: true,
                                })
                              }}
                              data-test='enable-status'
                            >
                              Enable
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              disabled={
                                !isEdit
                                  ? !data.enabled ||
                                    !hasPermission(
                                      permissions,
                                      PermissionsEnum.CanEditPaymentProviders
                                    )
                                  : !data.enabled
                              }
                              onSelect={() => {
                                handleSubmit({
                                  ...data,
                                  enabled: false,
                                })
                              }}
                              data-test='disable-status'
                            >
                              Disable
                            </DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </ButtonGroup>
                </FormBaseButtonActionWrapper>
              </FormBaseNavigationHeader>

              <MultiSelect
                name='languages'
                placeholder='Country'
                size='lg'
                disabled={!isEdit || formikProps.isSubmitting}
                value={formikProps.values.languages.map((lang) => lang.id)}
                onChange={(selectedIds: string[]) => {
                  const selectedLanguages = languages.filter((lang) =>
                    selectedIds.includes(lang.id)
                  )
                  formikProps.setFieldValue('languages', selectedLanguages)
                }}
                fullWidth
              >
                {availableLanguages.map((lang) => (
                  <Option value={lang.id} key={lang.id}>
                    {lang.name}
                  </Option>
                ))}
                {formikProps.values.languages.map((lang) => (
                  <Option value={lang.id} key={lang.id}>
                    {lang.name}
                  </Option>
                ))}
              </MultiSelect>
            </Form>
          )
        }}
      </Formik>
    </React.Fragment>
  )
}
